import { Close as IconClose, ErrorOutline as IconError } from "@mui/icons-material";
import { Alert, Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatSeconds, formatTimeRange } from "~/modules/app/util/formatters";

import { LONG_BIKE_ROUTE_THRESHOLD_SECONDS } from "../../constants/travelMethods";
import { IconFieldworker, IconFieldworkerProps } from "../IconFieldworker";
import { VisitTravelGroupsOverviewShiftFragment } from "./fragments.generated";

type ShiftRowProps = {
    shift: VisitTravelGroupsOverviewShiftFragment;
    isOwner: boolean;
    transportMethod: IconFieldworkerProps["variant"];
    onRemoveClick: () => void;
    isBeingRemoved?: boolean;
};

export function ShiftRow({ shift, isOwner, transportMethod, onRemoveClick, isBeingRemoved = false }: ShiftRowProps) {
    const { t } = useTranslation("plannerV2");
    const fullName = `${shift.fieldworker.user.firstName} ${shift.fieldworker.user.lastName}`;

    const shiftStartsOrEndsWithLongBikeRide =
        transportMethod === "bike" &&
        shift.plannerShiftTravels.some(
            plannerShiftTravel => plannerShiftTravel.plannerTravel.durationInSeconds > LONG_BIKE_ROUTE_THRESHOLD_SECONDS,
        );

    return (
        <Stack>
            <Stack
                direction="row"
                component="li"
                padding={2}
                gap={2}
                justifyContent="space-between"
                sx={theme => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    opacity: isBeingRemoved ? 0.5 : 1,
                })}
            >
                <Stack direction="row" columnGap={2}>
                    <Box>
                        <IconFieldworker variant={transportMethod} />
                    </Box>
                    <Stack>
                        <Typography sx={{ textDecoration: isBeingRemoved ? "line-through" : "none" }}>{fullName}</Typography>
                        <Typography variant="caption">
                            {formatTimeRange(parseStringToDate(shift.startTime), parseStringToDate(shift.endTime))} (
                            {formatSeconds(shift.durationInSeconds)}) {t(`visitDetail.travelMethod.${transportMethod}`)}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" columnGap={1}>
                    {isOwner && <Chip label={t("visitDetail.driver")} />}
                    <IconButton
                        size="small"
                        disabled={isBeingRemoved}
                        onClick={onRemoveClick}
                        aria-label={t("visitDetail.removeFieldworker", { name: fullName })}
                    >
                        <IconClose fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>
            {shiftStartsOrEndsWithLongBikeRide && (
                <Alert color="error" icon={<IconError />} sx={{ alignItems: "center" }}>
                    <Typography variant="body2">
                        {t("visitDetail.longBikeRideWarning", { minutes: LONG_BIKE_ROUTE_THRESHOLD_SECONDS / 60 })}
                    </Typography>
                </Alert>
            )}
        </Stack>
    );
}
